import * as Phaser from "phaser-ce"
import { AppRoutes, browserHistory } from "../../common/AppsRouter"
import { CCUtils, FirebaseEnv } from "../../common/CCUtils"
import { log } from "../../common/Logging"
import { ActivityProgressStoreSA } from "../../react/activity-progress/ActivityProgressStoreSA"
import { StudentStoreSA } from "../../react/students/StudentStoreSA"
import { ActivityKey } from "../../shared/models/ActivityKey"
import { Transitions } from "../activities/transitions/Transitions"
import { GameState, TransitionState } from "../GameState"
import { Buttons, ButtonSize, ButtonType } from "../generic-sprites/Buttons"
import { Fireworks } from "../generic-sprites/Fireworks"
import { loadGenericAssets } from "../generic-sprites/GenericAssets"
import { Audio } from "../utils/Audio"
import { ColorUtils } from "../utils/ColorUtils"
import { PhaserUtils } from "../utils/PhaserUtils"
import { Scale } from "../utils/Scale"
import { Sprites } from "../utils/Sprites"
import { Videos } from "../utils/Videos"
import {
    loadAssets,
    MapImages,
    MapVideo,
    transitionAudioKey,
    welcomeVideoKey,
} from "./MapAssets"
import { MapBuilding } from "./sprites/MapBuilding"

export class MapState extends Phaser.State {
    static goTo?: GameState
    gameProgressStates: Map<ActivityKey, GameStateInfo>

    addictionMaze: MapBuilding
    arcade: MapBuilding
    campCravings: MapBuilding
    hospital: MapBuilding
    house: MapBuilding
    kwit: MapBuilding
    news: MapBuilding
    school: MapBuilding
    park: MapBuilding
    scienceMuseum: MapBuilding
    theater: MapBuilding
    orangeRoofApartment: MapBuilding

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
        if (ActivityProgressStoreSA.instance.activitiesComplete()) {
            Fireworks.loadFireworks(this.game)
        }
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        // A little hacky, but until / unless we integrate activity into the URL this is the best I can think of.
        if (MapState.goTo) {
            log.debug(`Going to ${MapState.goTo}`)
            this.state.start(MapState.goTo)
            MapState.goTo = undefined
            return
        }

        Scale.resizeScreen(this.game, true)
        ColorUtils.setBackgroundColor(this.game, "#9ea150")

        const scale = 0.32

        const allMapSprites = this.game.add.group()

        const background = Sprites.add(this.game, MapImages.BACKGROUND, {
            scale,
            group: allMapSprites,
        })
        Sprites.centerInWorld(background)
        background.y = background.y - Scale.availableHeight() * 0.01

        const mapBuildingFactory = MapBuilding.mapBuildingFactory(
            this.game,
            scale,
            allMapSprites,
        )

        let buttonSize = ButtonSize.WIDE

        const playTransitionAudioAndZoom = (
            activityKey: string,
            gameState: ActivityKey,
            zoomSprite: MapBuilding,
        ) => {
            setTimeout(() => {
                Audio.add(this.game, transitionAudioKey(activityKey), {
                    onStop: () => {
                        this.game.input.enabled = true
                    },
                })
                zoomSprite.activate(Transitions.intro(this.game, gameState))
            }, 1000)
        }

        const makeFireworks = async () => {
            allMapSprites.visible = false
            const congratsVideo = await Videos.add(
                this.game,
                MapVideo.CONGRATS,
                {
                    fillScreen: true,
                    onComplete: () => {
                        allMapSprites.visible = true
                        congratsVideo.destroy()

                        const randomLocation = () =>
                            Math.random() * (0.7 - 0.3) + 0.3
                        const playFireworks = setInterval(() => {
                            Fireworks.play(
                                this.game,
                                Scale.x(randomLocation()),
                                Scale.y(randomLocation()),
                            )
                        }, 1200)

                        setTimeout(() => {
                            clearInterval(playFireworks)
                            Buttons.create(
                                this.game,
                                "Exit",
                                () => browserHistory.push(AppRoutes.home),
                                { y: Scale.y(0.9), centerInWorldX: true },
                            )
                        }, 10000)
                    },
                },
            )
        }

        const startNextActivity = async (
            activityKey?: ActivityKey | "fireworks",
        ) => {
            const nextActivityKey =
                ActivityProgressStoreSA.instance.getNextActivityKey(activityKey)

            function doOnComplete(
                game: Phaser.Game,
                gameState: ActivityKey,
                zoomSprite: MapBuilding,
            ) {
                const handleWelcomeEnd = () => {
                    game.input.enabled = false
                    allMapSprites.visible = true
                    playTransitionAudioAndZoom(
                        nextActivityKey,
                        gameState,
                        zoomSprite,
                    )
                }

                if (nextActivityKey === ActivityKey.TIME_MACHINE) {
                    game.input.enabled = true
                    Buttons.create(game, "Next", handleWelcomeEnd, {
                        x: Scale.x(0.874),
                        y: Scale.y(0.94),
                    })
                } else {
                    handleWelcomeEnd()
                }
            }

            if (nextActivityKey === "fireworks") {
                makeFireworks()
            } else {
                const state = this.gameProgressStates.get(nextActivityKey)
                const gameState = state.state
                const zoomSprite = state.building
                this.game.input.enabled = false
                if (state.showWelcome) {
                    allMapSprites.visible = false
                    const welcomeVideo = await Videos.add(
                        this.game,
                        welcomeVideoKey(nextActivityKey),
                        {
                            onComplete: () => {
                                if (welcomeVideo) {
                                    welcomeVideo.destroy()
                                }
                                doOnComplete(this.game, gameState, zoomSprite)
                            },
                            onException: () => {
                                doOnComplete(this.game, gameState, zoomSprite)
                            },
                            fillScreen: true,
                        },
                    )
                } else {
                    playTransitionAudioAndZoom(
                        nextActivityKey,
                        gameState,
                        zoomSprite,
                    )
                }
            }
        }

        const makeZoomButton = (
            key: ActivityKey,
            name: string,
            position: number,
            xPos: number,
            demo: boolean,
        ) => {
            Buttons.create(this.game, name, () => startNextActivity(key), {
                x: Scale.x(0.03 + xPos * 0.125),
                y: Scale.y(0.03 + position * 0.05),
                buttonSize,
                buttonType: demo ? ButtonType.SECONDARY : ButtonType.PRIMARY,
                scale: 0.6,
                group: allMapSprites,
            })
        }

        this.campCravings = mapBuildingFactory(
            Scale.x(0.322),
            Scale.y(0.126),
            MapImages.CAMP_CRAVINGS,
        )
        this.addictionMaze = mapBuildingFactory(
            Scale.x(0.585),
            Scale.y(0.26),
            MapImages.ADDICTION_MAZE,
        )
        mapBuildingFactory(Scale.x(0.158), Scale.y(0.304), MapImages.TURBINES)
        mapBuildingFactory(Scale.x(0.02), Scale.y(0.4), MapImages.FARM)
        mapBuildingFactory(Scale.x(0.341), Scale.y(0.385), MapImages.CELL_TOWER)
        this.orangeRoofApartment = mapBuildingFactory(
            Scale.x(0.206),
            Scale.y(0.397),
            MapImages.BIG_HOUSE_2,
        )
        mapBuildingFactory(Scale.x(0.143), Scale.y(0.467), MapImages.BIG_HOUSE)
        this.scienceMuseum = mapBuildingFactory(
            Scale.x(0.39),
            Scale.y(0.37),
            MapImages.SCIENCE_MUSEUM,
        )
        this.house = mapBuildingFactory(
            Scale.x(0.33),
            Scale.y(0.515),
            MapImages.HOUSE_2,
        )
        mapBuildingFactory(Scale.x(0.268), Scale.y(0.556), MapImages.HOUSE_3)
        mapBuildingFactory(Scale.x(0.221), Scale.y(0.594), MapImages.HOUSE)
        this.arcade = mapBuildingFactory(
            Scale.x(0.633),
            Scale.y(0.436),
            MapImages.ARCADE,
        )
        mapBuildingFactory(Scale.x(0.59), Scale.y(0.48), MapImages.BUILDING_2)
        mapBuildingFactory(Scale.x(0.551), Scale.y(0.497), MapImages.LIBRARY)
        this.theater = mapBuildingFactory(
            Scale.x(0.508),
            Scale.y(0.529),
            MapImages.THEATER,
        )
        this.hospital = mapBuildingFactory(
            Scale.x(0.443),
            Scale.y(0.556),
            MapImages.HOSPITAL,
        )
        mapBuildingFactory(Scale.x(0.402), Scale.y(0.618), MapImages.BUILDING)
        this.news = mapBuildingFactory(
            Scale.x(0.309),
            Scale.y(0.626),
            MapImages.NEWS,
        )
        this.school = mapBuildingFactory(
            Scale.x(0.787),
            Scale.y(0.379),
            MapImages.SCHOOL,
        )
        this.park = mapBuildingFactory(
            Scale.x(0.656),
            Scale.y(0.454),
            MapImages.PARK,
        )
        this.kwit = mapBuildingFactory(
            Scale.x(0.564),
            Scale.y(0.531),
            MapImages.KWIT,
        )
        mapBuildingFactory(Scale.x(0.631), Scale.y(0.568), MapImages.BUILDING_3)
        mapBuildingFactory(Scale.x(0.446), Scale.y(0.64), MapImages.TREES)

        const sign = Sprites.add(this.game, MapImages.SIGN, {
            x: Scale.width() * 0.05,
            debug: true,
            scale: 0.7,
            group: allMapSprites,
        })
        sign.y += document.documentElement.clientHeight - sign.height

        const currentEnv = CCUtils.firebaseEnv()
        const demoButtonsXPosition = currentEnv === FirebaseEnv.DEMO ? 0 : 2
        const nonDemoButtonsXPosition = 0
        const grade6ButtonsXPosition = 1

        this.gameProgressStates = new Map([
            // Grade 5, Lesson 1
            [
                ActivityKey.ECIG_101,
                {
                    buttonName: "Ecig 101",
                    state: ActivityKey.ECIG_101,
                    building: this.scienceMuseum,
                    showWelcome: true,
                    position: 1,
                    xPos: demoButtonsXPosition,
                    demo: true,
                },
            ],
            [
                ActivityKey.TOBACCO_TOUR,
                {
                    buttonName: "Tob Tour",
                    state: ActivityKey.TOBACCO_TOUR,
                    building: this.scienceMuseum,
                    showWelcome: false,
                    position: 1,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],

            // Grade 5, Lesson 2
            [
                ActivityKey.KIDS_CHOICE,
                {
                    buttonName: "Kids' Choice",
                    state: ActivityKey.KIDS_CHOICE,
                    building: this.theater,
                    showWelcome: true,
                    position: 2,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.MAKE_YOUR_OWN_VAPER_PT_1,
                {
                    buttonName: "Make Vaper P1",
                    state: ActivityKey.MAKE_YOUR_OWN_VAPER_PT_1,
                    building: this.school,
                    showWelcome: false,
                    position: 3,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.KIDS_CHOICE_2,
                {
                    buttonName: "Kids' Choice 2",
                    state: ActivityKey.KIDS_CHOICE_2,
                    building: this.theater,
                    showWelcome: false,
                    position: 4,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.REALITY_CHECK,
                {
                    buttonName: "Reality Check",
                    state: ActivityKey.REALITY_CHECK,
                    building: this.kwit,
                    showWelcome: false,
                    position: 2,
                    xPos: demoButtonsXPosition,
                    demo: true,
                },
            ],

            // Grade 5, Lesson 3
            [
                ActivityKey.MAKE_YOUR_OWN_VAPER_PT_2,
                {
                    buttonName: "Make Vaper P2",
                    state: ActivityKey.MAKE_YOUR_OWN_VAPER_PT_2,
                    building: this.school,
                    showWelcome: true,
                    position: 5,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.ADDICTION_MAIN,
                {
                    buttonName: "Addiction 101",
                    state: ActivityKey.ADDICTION_MAIN,
                    building: this.scienceMuseum,
                    showWelcome: false,
                    position: 3,
                    xPos: demoButtonsXPosition,
                    demo: true,
                },
            ],
            [
                ActivityKey.ADDICTION_PONG,
                {
                    buttonName: "Addiction Pong",
                    state: ActivityKey.ADDICTION_PONG,
                    building: this.arcade,
                    showWelcome: false,
                    position: 4,
                    xPos: demoButtonsXPosition,
                    demo: true,
                },
            ],

            // Grade 5, Lesson 4
            [
                ActivityKey.CAMP_CRAVINGS,
                {
                    buttonName: "Camp Cravings",
                    state: ActivityKey.CAMP_CRAVINGS,
                    building: this.campCravings,
                    showWelcome: true,
                    position: 6,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.PERSONALITY,
                {
                    buttonName: "Personality",
                    state: ActivityKey.PERSONALITY,
                    building: this.news,
                    showWelcome: false,
                    position: 7,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],

            // Grade 5, Lesson 5
            [
                ActivityKey.SECOND_HAND,
                {
                    buttonName: "2nd Hand 101",
                    state: ActivityKey.SECOND_HAND,
                    building: this.scienceMuseum,
                    showWelcome: true,
                    position: 5,
                    xPos: demoButtonsXPosition,
                    demo: true,
                },
            ],
            [
                ActivityKey.SUPER_HERO,
                {
                    buttonName: "Super Hero",
                    state: ActivityKey.SUPER_HERO,
                    building: this.house,
                    showWelcome: false,
                    position: 8,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.TRUTH_OR_DARE,
                {
                    buttonName: "Truth or Dare",
                    state: ActivityKey.TRUTH_OR_DARE,
                    building: this.orangeRoofApartment,
                    showWelcome: false,
                    position: 9,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],

            // Grade 5, Lesson 6
            [
                ActivityKey.MAKE_A_VIDEO,
                {
                    buttonName: "Make A Video",
                    state: ActivityKey.MAKE_A_VIDEO,
                    building: this.kwit,
                    showWelcome: true,
                    position: 10,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.WHEEL_OF_MISFORTUNE,
                {
                    buttonName: "Wheel of M",
                    state: ActivityKey.WHEEL_OF_MISFORTUNE,
                    building: this.kwit,
                    showWelcome: false,
                    position: 11,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.EVERY_CIG,
                {
                    buttonName: "Every Cig Pt 1",
                    state: ActivityKey.EVERY_CIG,
                    building: this.scienceMuseum,
                    showWelcome: false,
                    position: 12,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],

            // Grade 5, Lesson 7
            [
                ActivityKey.DEFINITION,
                {
                    buttonName: "Def of Smoker",
                    state: ActivityKey.DEFINITION,
                    building: this.school,
                    showWelcome: true,
                    position: 13,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.MAZE,
                {
                    buttonName: "Maze",
                    state: ActivityKey.MAZE,
                    building: this.addictionMaze,
                    showWelcome: false,
                    position: 14,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.EVERY_CIG_2,
                {
                    buttonName: "Every Cig Pt 2",
                    state: ActivityKey.EVERY_CIG_2,
                    building: this.scienceMuseum,
                    showWelcome: false,
                    position: 15,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],

            // Grade 5, Lesson 8
            [
                ActivityKey.TIME_MACHINE,
                {
                    buttonName: "Time machine",
                    state: ActivityKey.TIME_MACHINE,
                    building: this.arcade,
                    showWelcome: true,
                    position: 16,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.SUMMARY,
                {
                    buttonName: "Review",
                    state: ActivityKey.SUMMARY,
                    building: this.school,
                    showWelcome: false,
                    position: 17,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.PLAYGROUND,
                {
                    buttonName: "Playground",
                    state: ActivityKey.PLAYGROUND,
                    building: this.school,
                    showWelcome: false,
                    position: 18,
                    xPos: nonDemoButtonsXPosition,
                    demo: false,
                },
            ],

            // Grade 6, Lesson 1
            [
                ActivityKey.ECIG_201,
                {
                    buttonName: "Ecig 201",
                    state: ActivityKey.ECIG_201,
                    building: this.scienceMuseum,
                    showWelcome: true,
                    position: 1,
                    xPos: grade6ButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.MYSTERY,
                {
                    buttonName: "Mystery",
                    state: ActivityKey.MYSTERY,
                    building: this.hospital,
                    showWelcome: false,
                    position: 2,
                    xPos: grade6ButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.DR_TIME,
                {
                    buttonName: "Dr. Time",
                    state: ActivityKey.DR_TIME,
                    building: this.hospital,
                    showWelcome: false,
                    position: 3,
                    xPos: grade6ButtonsXPosition,
                    demo: false,
                },
            ],

            // Grade 6, Lesson 2
            [
                ActivityKey.ADDICTION_201,
                {
                    buttonName: "Addiction 201",
                    state: ActivityKey.ADDICTION_201,
                    building: this.scienceMuseum,
                    showWelcome: true,
                    position: 4,
                    xPos: grade6ButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.ADDICTION_ROAD,
                {
                    buttonName: "Addiction Road",
                    state: ActivityKey.ADDICTION_ROAD,
                    building: this.arcade,
                    showWelcome: false,
                    position: 5,
                    xPos: grade6ButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.MAKE_A_FRIEND,
                {
                    buttonName: "Make a Friend",
                    state: ActivityKey.MAKE_A_FRIEND,
                    building: this.park,
                    showWelcome: false,
                    position: 6,
                    xPos: grade6ButtonsXPosition,
                    demo: false,
                },
            ],
            [
                ActivityKey.SUMMARY_6,
                {
                    buttonName: "Summary 6",
                    state: ActivityKey.SUMMARY_6,
                    building: this.school,
                    showWelcome: false,
                    position: 7,
                    xPos: grade6ButtonsXPosition,
                    demo: false,
                },
            ],
        ])

        const isAdmin = StudentStoreSA.instance.isAdmin()
        log.debug("Student is admin in Map State? " + isAdmin)
        // Show the buttons to admins only, but never in Demo Mode
        if (currentEnv !== FirebaseEnv.DEMO && isAdmin) {
            Buttons.create(
                this.game,
                "Activities",
                () => this.game.state.start(TransitionState.WORLD),
                {
                    x: Scale.x(0.03),
                    y: Scale.y(0.03),
                    buttonSize: ButtonSize.WIDE,
                    scale: 0.6,
                    group: allMapSprites,
                },
            )

            Buttons.create(
                this.game,
                "Fireworks",
                () => {
                    startNextActivity("fireworks")
                },
                {
                    x: Scale.x(0.03 + grade6ButtonsXPosition * 0.125),
                    y: Scale.y(0.03),
                    buttonSize: ButtonSize.WIDE,
                    scale: 0.6,
                    group: allMapSprites,
                },
            )

            const nonDemoActivityKeys = [
                ActivityKey.TOBACCO_TOUR,
                ActivityKey.KIDS_CHOICE,
                ActivityKey.MAKE_YOUR_OWN_VAPER_PT_1,
                ActivityKey.KIDS_CHOICE_2,
                ActivityKey.MAKE_YOUR_OWN_VAPER_PT_2,
                ActivityKey.CAMP_CRAVINGS,
                ActivityKey.PERSONALITY,
                ActivityKey.SUPER_HERO,
                ActivityKey.TRUTH_OR_DARE,
                ActivityKey.MAKE_A_VIDEO,
                ActivityKey.WHEEL_OF_MISFORTUNE,
                ActivityKey.EVERY_CIG,
                ActivityKey.DEFINITION,
                ActivityKey.MAZE,
                ActivityKey.EVERY_CIG_2,
                ActivityKey.TIME_MACHINE,
                ActivityKey.PLAYGROUND,
                ActivityKey.SUMMARY,
            ]
            nonDemoActivityKeys.forEach((key: ActivityKey) => {
                const state = this.gameProgressStates.get(key)
                makeZoomButton(
                    key,
                    state.buttonName,
                    state.position,
                    state.xPos,
                    state.demo,
                )
            })

            const grade6ActivityKeys = [
                ActivityKey.ECIG_201,
                ActivityKey.MYSTERY,
                ActivityKey.DR_TIME,
                ActivityKey.ADDICTION_201,
                ActivityKey.ADDICTION_ROAD,
                ActivityKey.MAKE_A_FRIEND,
                ActivityKey.SUMMARY_6,
            ]
            grade6ActivityKeys.forEach((key: ActivityKey) => {
                const state = this.gameProgressStates.get(key)
                makeZoomButton(
                    key,
                    state.buttonName,
                    state.position,
                    state.xPos,
                    state.demo,
                )
            })
        }

        // Show demo buttons in Demo Mode, or to admins all the time
        if (currentEnv === FirebaseEnv.DEMO || isAdmin) {
            buttonSize = ButtonSize.EXTRA_WIDE

            const demoActivityKeys = [
                ActivityKey.ECIG_101,
                ActivityKey.REALITY_CHECK,
                ActivityKey.SECOND_HAND,
                ActivityKey.ADDICTION_MAIN,
                ActivityKey.ADDICTION_PONG,
            ]
            demoActivityKeys.forEach((key: ActivityKey) => {
                const state = this.gameProgressStates.get(key)
                makeZoomButton(
                    key,
                    state.buttonName,
                    state.position,
                    state.xPos,
                    state.demo,
                )
            })

            if (currentEnv === FirebaseEnv.DEMO) {
                Buttons.create(
                    this.game,
                    "Reset Demo",
                    () => {
                        localStorage.clear()
                        window.location.reload()
                    },
                    {
                        x: Scale.x(0.03),
                        y: Scale.y(0.03 + 6 * 0.05),
                        buttonSize: ButtonSize.EXTRA_WIDE,
                        buttonType: ButtonType.PRIMARY,
                        scale: 0.6,
                        debug: true,
                        group: allMapSprites,
                    },
                )
            }
        }

        // If you're a student not in the demo, start the next activity
        if (currentEnv !== FirebaseEnv.DEMO && !isAdmin) {
            startNextActivity()
        }
    }
}

interface GameStateInfo {
    buttonName: string
    state: ActivityKey
    building: MapBuilding
    showWelcome: boolean
    position: number
    xPos: number
    demo: boolean
}
